import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './GuildCreation.module.css';

interface EmblemConfig {
  animalIcon: string;
  animalHue: number; // 0-360 for hue rotation
  animalSaturation: number; // 50-150 for saturation adjustment
  animalBrightness: number; // 80-120 for brightness adjustment
  shieldType: string; // Shield background type
}

const ANIMAL_ICONS = [
  { name: 'Bear', file: 'Bear.png' },
  { name: 'Boar', file: 'Boar.png' },
  { name: 'Lynx', file: 'Lynx.png' },
  { name: 'Ox', file: 'Ox.png' },
  { name: 'Raven', file: 'Raven.png' },
  { name: 'Stag', file: 'Stag.png' },
  { name: 'Wolf', file: 'Wolf.png' }
];

const SHIELD_TYPES = [
  { name: 'Standard', file: 'Shield.png' },
  { name: 'Golden', file: 'Shield Golden.png' },
  { name: 'Gray', file: 'Shield Gray.png' },
  { name: 'Sunbath', file: 'Shield Sunbath.png' },
  { name: 'Tint', file: 'Shield Tint.png' }
];

const PRESET_COLORS = [
  { name: 'Natural', hue: 0, saturation: 0, brightness: 150 },
  { name: 'Red', hue: 0, saturation: 130, brightness: 100 },
  { name: 'Orange', hue: 30, saturation: 130, brightness: 100 },
  { name: 'Gold', hue: 45, saturation: 120, brightness: 110 },
  { name: 'Green', hue: 120, saturation: 120, brightness: 100 },
  { name: 'Teal', hue: 180, saturation: 120, brightness: 100 },
  { name: 'Blue', hue: 210, saturation: 130, brightness: 100 },
  { name: 'Purple', hue: 270, saturation: 130, brightness: 100 },
  { name: 'Pink', hue: 330, saturation: 120, brightness: 110 },
  { name: 'Shadow', hue: 0, saturation: 50, brightness: 80 },
  { name: 'Ghost', hue: 0, saturation: 30, brightness: 120 },
  { name: 'Silver', hue: 0, saturation: 0, brightness: 110 }
];

const GuildCreation: React.FC = () => {
  const navigate = useNavigate();
  const [guildName, setGuildName] = useState('');
  const [guildTag, setGuildTag] = useState('');
  const [emblem, setEmblem] = useState<EmblemConfig>({
    animalIcon: 'Wolf',
    animalHue: 0,
    animalSaturation: 100,
    animalBrightness: 100,
    shieldType: 'Shield.png'
  });
  const [hasGuildToken, setHasGuildToken] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    checkGuildToken();
  }, []);

  const checkGuildToken = async () => {
    try {
      const response = await fetch('/api/guild/check-guild-token', {
        credentials: 'include'
      });
      const data = await response.json();
      setHasGuildToken(data.hasToken);
    } catch (error) {
      console.error('Error checking guild token:', error);
    }
  };

  const handleEmblemChange = (field: keyof EmblemConfig, value: string | number) => {
    setEmblem(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getAnimalColor = () => {
    // Convert hue/saturation/brightness to HSL color
    const hue = emblem.animalHue;
    const saturation = emblem.animalSaturation;
    const lightness = (emblem.animalBrightness / 100) * 50; // Map 50-150 to 25-75 lightness
    
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  const handleCreateGuild = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!hasGuildToken) {
      setError('You need a Guild Token to create a guild');
      return;
    }

    if (guildName.length < 3 || guildName.length > 30) {
      setError('Guild name must be between 3 and 30 characters');
      return;
    }

    if (guildTag.length < 2 || guildTag.length > 5) {
      setError('Guild tag must be between 2 and 5 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/guild/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          name: guildName,
          tag: guildTag,
          emblemAnimalIcon: emblem.animalIcon,
          emblemAnimalHue: emblem.animalHue,
          emblemAnimalSaturation: emblem.animalSaturation,
          emblemAnimalBrightness: emblem.animalBrightness,
          emblemShieldType: emblem.shieldType
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Redirect to guild dashboard
        navigate('/guild');
      } else {
        setError(data.message || 'Failed to create guild');
      }
    } catch (error) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const renderEmblemPreview = () => {
    const shieldSrc = emblem.shieldType.startsWith('Shield ') 
      ? `/img/guild/Shield/${emblem.shieldType}` 
      : `/img/guild/Shield/${emblem.shieldType}`;
    
    return (
      <div className={styles.emblemPreview}>
        <img 
          src={shieldSrc}
          alt="Guild Shield"
          className={styles.shieldBackground}
        />
        <div 
          className={styles.animalIcon}
          style={{
            backgroundColor: getAnimalColor(),
            WebkitMaskImage: `url(/img/guild/${emblem.animalIcon}.png)`,
            maskImage: `url(/img/guild/${emblem.animalIcon}.png)`,
            WebkitMaskSize: 'contain',
            maskSize: 'contain',
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskPosition: 'center'
          }}
        />
      </div>
    );
  };

  if (!hasGuildToken) {
    return (
      <div className={styles.container}>
        <div className={styles.noToken}>
          <h2>Guild Creation</h2>
          <p>You need a Guild Token to create a guild.</p>
          <p>Guild Tokens can be obtained through special events & tournaments.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.creationForm}>
        <h2>Create Your Guild</h2>
        
        <form onSubmit={handleCreateGuild}>
          <div className={styles.formGroup}>
            <label htmlFor="guildName">Guild Name</label>
            <input
              type="text"
              id="guildName"
              value={guildName}
              onChange={(e) => setGuildName(e.target.value)}
              placeholder="Enter guild name (3-30 characters)"
              maxLength={30}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="guildTag">Guild Tag</label>
            <input
              type="text"
              id="guildTag"
              value={guildTag}
              onChange={(e) => setGuildTag(e.target.value.toUpperCase())}
              placeholder="Enter guild tag (2-5 characters)"
              maxLength={5}
              required
            />
          </div>

          <div className={styles.emblemBuilder}>
            <h3>Design Your Guild Emblem</h3>
            
            <div className={styles.controlGroup}>
              <label>Choose Your Shield</label>
              <div className={styles.shieldGrid}>
                {SHIELD_TYPES.map(shield => (
                  <button
                    key={shield.name}
                    type="button"
                    className={`${styles.shieldButton} ${emblem.shieldType === shield.file ? styles.selected : ''}`}
                    onClick={() => handleEmblemChange('shieldType', shield.file)}
                    title={shield.name}
                  >
                    <img 
                      src={`/img/guild/Shield/${shield.file}`} 
                      alt={shield.name}
                      className={styles.shieldButtonImg}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.controlGroup}>
              <label>Choose Your Animal</label>
              <div className={styles.animalGrid}>
                {ANIMAL_ICONS.map(animal => (
                  <button
                    key={animal.name}
                    type="button"
                    className={`${styles.animalButton} ${emblem.animalIcon === animal.name ? styles.selected : ''}`}
                    onClick={() => handleEmblemChange('animalIcon', animal.name)}
                    title={animal.name}
                  >
                    <img 
                      src={`/img/guild/${animal.file}`} 
                      alt={animal.name}
                      className={styles.animalButtonImg}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.previewAndControls}>
              <div className={styles.emblemPreviewContainer}>
                {renderEmblemPreview()}
              </div>

              <div className={styles.sliderControls}>
                <div className={styles.controlGroup}>
                  <label>Hue: {emblem.animalHue}°</label>
                  <input 
                    type="range"
                    min="0"
                    max="360"
                    value={emblem.animalHue}
                    onChange={(e) => handleEmblemChange('animalHue', parseInt(e.target.value))}
                    className={styles.hueSlider}
                  />
                </div>

                <div className={styles.controlGroup}>
                  <label>Saturation: {emblem.animalSaturation}%</label>
                  <input 
                    type="range"
                    min="0"
                    max="200"
                    value={emblem.animalSaturation}
                    onChange={(e) => handleEmblemChange('animalSaturation', parseInt(e.target.value))}
                    className={styles.saturationSlider}
                  />
                </div>

                <div className={styles.controlGroup}>
                  <label>Brightness: {emblem.animalBrightness}%</label>
                  <input 
                    type="range"
                    min="50"
                    max="150"
                    value={emblem.animalBrightness}
                    onChange={(e) => handleEmblemChange('animalBrightness', parseInt(e.target.value))}
                    className={styles.brightnessSlider}
                  />
                </div>
              </div>
            </div>

            <div className={styles.controlGroup}>
              <label>Animal Color Presets</label>
              <div className={styles.colorPresetGrid}>
                {PRESET_COLORS.map(preset => (
                  <button
                    key={preset.name}
                    type="button"
                    className={`${styles.colorPresetButton} ${emblem.animalHue === preset.hue && emblem.animalSaturation === preset.saturation && emblem.animalBrightness === preset.brightness ? styles.selected : ''}`}
                    onClick={() => {
                      handleEmblemChange('animalHue', preset.hue);
                      handleEmblemChange('animalSaturation', preset.saturation);
                      handleEmblemChange('animalBrightness', preset.brightness);
                    }}
                  >
                    <div 
                      className={styles.colorPreview}
                      style={{
                        backgroundColor: `hsl(${preset.hue}, ${preset.saturation}%, ${(preset.brightness / 100) * 50}%)`,
                        WebkitMaskImage: `url(/img/guild/${emblem.animalIcon}.png)`,
                        maskImage: `url(/img/guild/${emblem.animalIcon}.png)`,
                        WebkitMaskSize: 'contain',
                        maskSize: 'contain',
                        WebkitMaskRepeat: 'no-repeat',
                        maskRepeat: 'no-repeat',
                        WebkitMaskPosition: 'center',
                        maskPosition: 'center'
                      }}
                    />
                    <span className={styles.colorName}>{preset.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {error && <div className={styles.error}>{error}</div>}

          <button 
            type="submit" 
            className={styles.createButton}
            disabled={loading}
          >
            {loading ? 'Creating Guild...' : 'Create Guild'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default GuildCreation;
